import React from "react";
import { Link } from "react-router-dom";
import type { IconType } from "react-icons";
import {
  IoCafeOutline,
  IoCashOutline,
  IoHandLeftOutline,
  IoKeyOutline,
  IoRepeatOutline,
  IoRestaurantOutline,
} from "react-icons/io5";
import styles from "./Technologies3DPrinting.module.css";

const STATS: { icon: IconType; value: string; label: string; sub: string }[] = [
  {
    icon: IoRepeatOutline,
    value: "Fast iteration",
    label: "Process",
    sub: "Prototype, improve, produce",
  },
  {
    icon: IoCashOutline,
    value: "Lower cost",
    label: "Production",
    sub: "Printed on demand, less stock",
  },
  {
    icon: IoHandLeftOutline,
    value: "Better fit",
    label: "Design",
    sub: "Geometry adjusted for grip",
  },
];

const OBJECTS: { icon: IconType; title: string; text: string }[] = [
  {
    icon: IoCafeOutline,
    title: "Mug grip",
    text: "Build confidence through familiar forms.",
  },
  {
    icon: IoRestaurantOutline,
    title: "Bowl stability",
    text: "Encourage repetition with purpose.",
  },
  {
    icon: IoKeyOutline,
    title: "Key rotation",
    text: "Support fine-motor and functional movement.",
  },
];

const Technologies3DPrinting: React.FC = () => {
  return (
    <main id="main" className="ui-page">
      <div className="ui-container">
        <div className={styles.heroBlock}>
          <section className="ui-hero-panel ui-hero-panel--split" aria-labelledby="t3d-hero-title">
            <div className="ui-hero-copy">
              <span className="ui-section-label">3D printing</span>
              <h1 id="t3d-hero-title">Practice accessories, printed on demand</h1>
              <p>Shaped for common hand sizes and grip styles, refined through quick digital iteration, and printed when you order.</p>
              <Link to="/shopping" className="ui-btn ui-btn--inverted">View the starter kit</Link>
            </div>
            <div className="ui-hero-media">
              <img src="/images/product/mug-and-bowl-3d-printer.png" alt="A 3D-printed mRehab practice mug on a table" loading="eager" fetchPriority="high" />
            </div>
          </section>
          <dl className="ui-fact-strip" aria-label="Key benefits">
            {STATS.map(({ icon: Icon, value, label, sub }) => (
              <div key={label} className="ui-fact">
                <Icon aria-hidden className="ui-fact-icon" />
                <dt className="ui-fact-label">{label}</dt>
                <dd className="ui-fact-value">{value}</dd>
                <dd className="ui-fact-sub">{sub}</dd>
              </div>
            ))}
          </dl>
        </div>

        <section className="ui-card ui-card--lg" aria-labelledby="geometry-title">
          <div className="ui-split">
            <div className={`ui-media ${styles.photo}`}>
              <img
                src="/images/product/bowl-still-shot.png"
                alt="Practicing with a 3D-printed bowl accessory at a table"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className={styles.text}>
              <h2 id="geometry-title">Tailored geometry</h2>
              <p>
                Everyone's hands and preferences differ. 3D printing lets us adjust
                accessory shapes for comfort and practice goals, without claiming
                medical customization.
              </p>
              <ul className="ui-bullets">
                <li>Adjusted for common hand sizes and grip styles</li>
                <li>Iterated quickly with digital modeling</li>
                <li>Used alongside optional in-app guidance</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="ui-card ui-card--lg" aria-labelledby="cost-title">
          <div className="ui-split ui-split--media-right">
            <div className={`ui-media ${styles.photo}`}>
              <img
                src="/images/product/mug-close-up.png"
                alt="Pouring water with a 3D-printed practice mug during a home session"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className={styles.text}>
              <h2 id="cost-title">A fraction of the cost</h2>
              <p>
                Specialty hardware can be costly to stock in bulk. With 3D printing,
                production can scale on demand with less waste when offerings are
                wellness accessories, not regulated devices.
              </p>
              <ul className="ui-bullets">
                <li>Designed for low-cost mass or on-demand printing</li>
                <li>Faster prototyping reduces time-to-improvement</li>
                <li>Distribution can be centralized or local</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="ui-card" aria-labelledby="objects-title">
          <div className="ui-card-header">
            <IoCafeOutline aria-hidden />
            <div className="ui-card-header-text">
              <h2 id="objects-title">Inspired by daily objects</h2>
              <p>
                Practice should not feel abstract. Our accessories borrow shapes from
                everyday objects so movement habits can connect to real tasks.
              </p>
            </div>
          </div>
          <ul className="ui-list">
            {OBJECTS.map(({ icon: Icon, title, text }) => (
              <li key={title} className="ui-list-row">
                <span className="ui-icon-chip" aria-hidden>
                  <Icon />
                </span>
                <div className="ui-list-text">
                  <span className="ui-list-label">{title}</span>
                  <span className="ui-list-desc">{text}</span>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className={`ui-card ui-card--lg ${styles.closing}`}>
          <h2>Iteration for real-world use</h2>
          <p className={styles.closingText}>
            From prototyping to production, additive manufacturing helps mRehab refine
            optional accessories based on how people actually practice at home, for
            wellness support only.
          </p>
          <div className={`ui-btn-row ${styles.closingRow}`}>
            <Link to="/how-it-works" className="ui-btn ui-btn--secondary">
              How it works
            </Link>
            <Link to="/contact" className="ui-btn ui-btn--ghost">
              Contact us
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Technologies3DPrinting;
